<template>
  <!-- 自定义抽屉 -->
  <van-popup v-model:show="drawerVisible" position="bottom" round :closeable="canClose"
    :close-on-click-overlay="canClose">
    <!--  弹窗内容 -->
    <div class="install-popup">
      <!-- 弹窗主体内容 -->
      <div class="popup-content">
        <!-- 标题 -->
        <h2 class="popup-title">{{ t('installPwa.title') }}</h2>

        <!-- 描述 -->
        <p class="popup-description">{{ t('installPwa.description') }}</p>

        <!-- 应用信息卡片 -->
        <div class="app-info-card">
          <img :src="imageUrl" alt="app icon" class="app-icon" />
          <span class="app-name">{{ PRODUCT_NAME }}</span>
        </div>

        <!-- 安装按钮区域 -->
        <div class="install-section">
          <button class="install-btn" @click="handleInstall" :disabled="isLoading" :style="{
            background: isLoading
              ? `linear-gradient(90deg, #4CAF50 ${percentage}%, #70C944 ${percentage}%)`
              : undefined
          }">
            <img v-if="!isLoading" src="@/assets/images/ic_download.svg" alt="download" class="download-icon" />
            <span v-if="isLoading">{{ Math.round(percentage) }}%</span>
            <span v-else>{{ t('installPwa.installButton') }}</span>
          </button>
          <div class="support-icon">
            <img src="@/assets/images/ic_headphones.svg" alt="support" />
          </div>
        </div>

        <!-- 安装步骤 -->
        <div class="install-steps" v-if="deviceInfo.isAndroid">
          <h3 class="steps-title">{{ t('installPwa.stepsTitle') }}</h3>
          <ol class="steps-list">
            <li>{{ t('installPwa.step1') }}</li>
            <li>{{ t('installPwa.step2') }}</li>
            <li>{{ t('installPwa.step3') }}</li>
            <li>{{ t('installPwa.step4') }}</li>
          </ol>
        </div>

        <!-- 友好提示 -->
        <div class="friendly-notice" v-if="deviceInfo.isAndroid">
          <div class="notice-header">
            <span class="notice-icon">!</span>
            <span class="notice-title">{{ t('installPwa.friendlyNoticeTitle') }}</span>
          </div>
          <p class="notice-text">{{ t('installPwa.friendlyNoticeText') }}</p>
        </div>
      </div>
    </div>
  </van-popup>


  <!-- iOS PWA 安装引导组件 -->
  <IosInstall v-model="showIosInstallGuide" :app-name="PRODUCT_NAME" :app-url="appUrl" :app-icon="appIcon"
    @close="handleIosInstallClose" />

  <ApkDownloadConfirm v-model:show="showApkDialog" />

</template>

<script setup>
// import { getInstallConfig } from '@/api/InstallPwa'
import { ref, onMounted, computed, watch, watchEffect } from 'vue'
import { getAssetsFile, downloadAPK } from '@/utils/pub-use'
import { useI18n } from 'vue-i18n'
import { getDeviceInfo, canInstallPWA, isPWAInstalled } from '@/utils/device'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/stores/index'
import { useUserStore } from '@/stores/modules/user'
import { trackPwaInstallPromptShown, trackPwaInstallPromptConfirmed, trackPwaInstallPromptDismissed, trackPwaInstalled } from '@/utils/firebase-analytics'
const userStore = useUserStore()
const { t } = useI18n()
const route = useRoute()
const appStore = useAppStore()
const imageUrl = ref(getAssetsFile('logo2.png'))
const PRODUCT_NAME = import.meta.env.VITE_APP_NAME
const showIosInstallGuide = ref(false)
const bootInstallOpen = ref(false)
const canClose = ref(false)
const deviceInfo = getDeviceInfo()
// APK下载弹窗状态
const showApkDialog = ref(false)
// 弹窗配置
const popTime = ref(0)
const popInterval = ref(0) // 弹窗时间间隔（小时）
// 应用信息配置
const appUrl = computed(() => {
  return window.location.hostname || 'Suer Win Ph.h21.com'
})

const appIcon = computed(() => {
  return 'logo.png'
})



const isInstalling = ref(false)
const percentage = ref(0)
const isLoading = ref(false)

const promptReady = ref(false)


const handleInstall = async () => {
  console.log('开始安装流程')
  // 1. 如果是iOS设备，弹出引导安装组件
  if (deviceInfo.isIOS) {
    console.log('iOS设备，显示安装引导')
    // drawerVisible.value = false
    showIosInstallGuide.value = true
    return
  }
  // 2. 判断是否支持PWA安装
  if (!canInstallPWA()) {
    console.log('不支持PWA安装，下载APK')
    await downloadAPK()
    drawerVisible.value = false
    return
  }

  // 3. 如果是Android设备，处理PWA安装
  // 或者桌面端
  if (deviceInfo.isAndroid || !deviceInfo.isMobile) {
    console.log('Android/PC设备，处理PWA安装')
    const installPrompt = window.installPrompt
    if (!installPrompt) {
      console.log('没有安装提示，下载APK')
      downloadAPK()
      drawerVisible.value = false
      return
    }
    trackPwaInstallPromptShown()
    try {
      const { outcome } = await installPrompt.prompt()

      if (outcome === 'accepted') {
        trackPwaInstallPromptConfirmed()
        console.log('用户接受安装')
        // 这里是模拟的安装进度
        // 用户确认安装后，开始显示进度条
        isLoading.value = true
        percentage.value = 0

        // 从0增长到100%的安装进度条效果
        const installTimer = setInterval(() => {
          const randomNum = Math.floor(Math.random() * 15 + 5) // 5-20的随机数
          if (percentage.value + randomNum >= 100) {
            percentage.value = 100
            clearInterval(installTimer)
            // 等待一秒后显示安装完成并关闭
            setTimeout(() => {
              percentage.value = 0
              console.log('安装完成')
              drawerVisible.value = false
              isLoading.value = false
              // 这里可以添加安装完成的提示
            }, 1000)
          } else {
            percentage.value += randomNum
          }
        }, 200) // 每200ms更新一次进度

      } else if (outcome === 'dismissed') {
        console.log('用户取消安装，显示APK下载弹框')
        // 显示APK下载确认弹窗
        showApkDialog.value = true
        trackPwaInstallPromptDismissed()
      }

    } catch (error) {
      console.error('安装过程出错:', error)
      // 重置状态并下载APK
      isLoading.value = false
      percentage.value = 0
      await downloadAPK()
    }

    return
  }

  // 4. 其他设备类型，默认下载APK
  console.log('其他设备类型，下载APK')
  downloadAPK()
  drawerVisible.value = false
}
// pwa 安装完成事件
window.addEventListener('appinstalled', () => {
  console.log('pwa 安装完成')
  // 这里才是真正的安装完成
  // drawerVisible.value = false
  trackPwaInstalled()
})

// iOS 安装引导关闭处理
const handleIosInstallClose = () => {
  showIosInstallGuide.value = false
  console.log('用户关闭了 iOS 安装引导')
}


// 检查是否应该显示弹窗（根据路由和popTime）
const shouldShowByRoute = ref(false)

// 检查弹窗时间间隔
const checkPopupInterval = () => {
  if (popInterval.value === 0) {
    console.log('弹窗时间间隔检查: 无限制 (popInterval = 0)')
    return true // 如果间隔为0，表示不限制时间间隔
  }

  const lastPopupTime = localStorage.getItem('lastPwaPopupTime')
  if (!lastPopupTime) {
    console.log('弹窗时间间隔检查: 首次显示')
    return true // 首次显示
  }

  const now = Date.now()
  const lastTime = parseInt(lastPopupTime)
  const intervalMs = popInterval.value * 60 * 60 * 1000 // 转换为毫秒
  const timeDiff = now - lastTime
  const canShow = timeDiff >= intervalMs

  console.log(`弹窗时间间隔检查: 间隔${popInterval.value}小时, 距离上次${Math.round(timeDiff / (1000 * 60 * 60) * 100) / 100}小时, 是否可显示: ${canShow}`)

  return canShow
}

// 记录弹窗显示时间
const recordPopupTime = () => {
  const now = Date.now()
  localStorage.setItem('lastPwaPopupTime', now.toString())
  console.log(`记录弹窗显示时间: ${new Date(now).toLocaleString()}`)
}

const drawerVisible = computed({
  get() {
    return !isPWAInstalled() && !window.platformGame && !window.jsBridge && promptReady.value && bootInstallOpen.value && shouldShowByRoute.value
  },
  set(val) {
    if (!val) {
      shouldShowByRoute.value = false
    } else {
      shouldShowByRoute.value = true
      // 当弹窗显示时，记录时间
      if (checkPopupInterval()) {
        recordPopupTime()
      }
    }
  }
})
const onSocketCharge = () => {
  const socket = window.$socket
  if (socket) {
    socket.on('recharge_notice', (data) => {
      // 检查时间间隔后再展示弹窗
      if (checkPopupInterval()) {
        shouldShowByRoute.value = true
        recordPopupTime()
      }
    })
  }
}

const onSocketFirstWithdraw = () => {
  const socket = window.$socket
  if (socket) {
    socket.on('first_withdraw_notice', (data) => {
      if (checkPopupInterval()) {
        shouldShowByRoute.value = true
        recordPopupTime()
      }
    })
  }
}

const startInterval = () => {
  // 写个定时任务，如果到了弹窗时间，则展示弹窗
  if (isPWAInstalled.value || window.platformGame || window.jsBridge) {
    return
  }
  // 10分钟检查一次
  setInterval(() => {
    if (checkPopupInterval()) {
      //当前路由是根路由
      if (popTime.value === 0 && (route.path === '/' || route.path === '')) {
        shouldShowByRoute.value = true
        recordPopupTime()
      } else {
        shouldShowByRoute.value = true
        recordPopupTime()
      }
    }
  }, 10 * 60 * 1000)
}

watchEffect(() => {
  const { bootInstall, popTime: configPopTime, popInterval: configPopInterval } = appStore.channelInfo || {}
  const { isSocketConnected } = appStore
  canClose.value = configPopInterval !== 0;
  popTime.value = configPopTime
  popInterval.value = configPopInterval
  bootInstallOpen.value = bootInstall
  if (bootInstall) {
    if (checkPopupInterval() && configPopTime === 0) {
      shouldShowByRoute.value = true
      recordPopupTime()
    }
    if (isSocketConnected) {
      if (popTime.value === 1) {
        onSocketCharge()
      }
      if (popTime.value === 2) {
        onSocketFirstWithdraw()
      }
    }
  }
})

// 监听路由变化
watch(() => route.path, (newPath) => {
  if (isPWAInstalled.value || window.platformGame || window.jsBridge) {
    return
  }
  // 当路由变化时，检查是否为根路径且popTime为0
  if ((newPath === '/' || newPath === '') && popTime.value === 0) {
    // 检查时间间隔后再设置显示状态
    if (checkPopupInterval()) {
      shouldShowByRoute.value = true
      recordPopupTime()
    }
  }
})


onMounted(() => {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    window.installPrompt = event
    promptReady.value = true
  })
  startInterval()
})

</script>

<style lang="scss">
.installing-dialog {
  background: #004D37;
}

// 安装弹窗样式
.install-popup {
  position: relative;
  width: 100%;
  // max-height: 536px !important;
  max-height: 100%;
  background: #fff;
  border-radius: 20px 20px 0 0;
  // overflow: hidden;
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
  background-image: url('@/assets/images/installPopBg.png');
  padding-top: 150px;
  /* 为背景图预留空间 */
}




.popup-bg {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
}



.popup-content {
  padding: 10px 20px;
  background: #fff;
  border-radius: 20px 20px 0 0;
  margin-top: -20px;
  /* 让内容区域稍微覆盖背景图 */
}

.popup-title {
  font-size: 18px;
  font-weight: bold;
  color: #000;
  text-align: center;
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.popup-description {
  font-size: 14px;
  color: #000;
  // text-align: center;
  margin: 0 0 10px 0;
  line-height: 1.4;
}

.app-info-card {
  display: flex;
  align-items: center;
  background: #f5f5f5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 15px;
}

.app-icon {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  margin-right: 12px;
}

.app-name {
  font-size: 16px;
  // font-weight: bold;
  color: #000;
}

.install-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.install-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 45px;
  background: linear-gradient(180deg, #70C944 0%, #337711 100%);
  color: #fff;
  border: none;
  border-radius: 45px;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  margin-right: 12px;
  text-align: center;
  transition: opacity 0.3s ease;
}

.install-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.download-icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
  filter: brightness(0) invert(1);
}

.support-icon {
  width: 40px;
  height: 40px;
  background: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.support-icon img {
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(0.5);
}

.install-steps {
  margin-bottom: 10px;
}

.steps-title {
  font-size: 16px;
  font-weight: bold;
  color: #000;
  margin: 0 0 12px 0;
}

.steps-list {
  margin: 0;
  // padding-left: 20px;
}

.steps-list li {
  font-size: 14px;
  color: #000;
  line-height: 1.4;
  margin-bottom: 4px;
}

.friendly-notice {
  background: #f8f8f8;
  padding: 10px;
  border-radius: 8px;
}

.notice-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.notice-icon {
  width: 16px;
  height: 16px;
  background: #000;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-right: 8px;
}

.notice-title {
  font-size: 14px;
  font-weight: bold;
  color: #000;
}

.notice-text {
  font-size: 12px;
  color: #000;
  line-height: 1.4;
  margin: 0;
}

// 原有的样式保持不变
.custom-pwa-drawer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999999;
  width: 100%;
  pointer-events: auto;
  display: flex;
  justify-content: center;
}

.drawer-mask {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999998;
}

.drawer-panel {
  position: absolute;
  bottom: 0;
  width: 100%;
  max-width: 750px;
  border-radius: 10px 10px 0 0;
  overflow: hidden;
  z-index: 999999;
  display: flex;
  flex-direction: column;
}

.drawer-body {
  padding: 24px 20px 20px 24px;
  background: #004D37;
  border-radius: 10px 10px 0 0;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.pwa-content {
  display: flex;
}

.pwa-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  margin-right: 16px;
  border: 1px solid #3cc35f;
  background: #fff;
  box-sizing: border-box;
}

.pwa-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
  color: #fff;
}

.pwa-desc {
  font-size: 12px;
  opacity: 0.85;
  color: #b0c4d9;
  line-height: 1.5;
}

.pwa-actions {
  display: flex;
  margin-top: 10px;
}

.cancel-btn {
  background: transparent;
  color: #8b8b8b;
  border: none;
  font-size: 13px;
  border-radius: 6px;
  padding: 4px 8px;
  margin-left: 100px;
}


// 开发环境测试按钮样式
.dev-test-panel {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 999999;
  display: flex;
  gap: 10px;
  background: #fff;
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.test-btn {
  background: #4CAF50;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #3d8b40;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(76, 175, 80, 0.2);
  }
}
</style>
